import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Edit2, Trash2, Clock, DollarSign, Tag, Sparkles, MoreHorizontal,
  Wand2, ChefHat, Scissors, Palette, Eye, Heart, Copy,
  GraduationCap, ShoppingBag, ArrowUpRight, Layers
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import ServiceAddonsModal from "./ServiceAddonsModal";

const DURATION_OPTIONS = [15, 30, 45, 60, 90, 120, 150, 180, 240];

const CATEGORY_ICONS = {
  makeup: Wand2, hair: Scissors, culinary: ChefHat,
  nails: Palette, lash: Eye, wellness: Heart
};

const CATEGORY_COLORS = {
  makeup: { bg: "bg-pink-50", border: "border-pink-200", text: "text-pink-700", accent: "bg-pink-100" },
  hair: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", accent: "bg-amber-100" },
  culinary: { bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-700", accent: "bg-orange-100" },
  nails: { bg: "bg-purple-50", border: "border-purple-200", text: "text-purple-700", accent: "bg-purple-100" },
  lash: { bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-700", accent: "bg-violet-100" },
  wellness: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", accent: "bg-emerald-100" },
};

const DEFAULT_COLORS = { bg: "bg-gray-50", border: "border-gray-200", text: "text-gray-700", accent: "bg-gray-100" };

const PLACEMENT_OPTIONS = [
  { id: "preference_card", label: "Preference Card", desc: "Show before booking" },
  { id: "confirmation", label: "Confirmation", desc: "Show after booking" },
  { id: "both", label: "Both", desc: "Before & after booking" },
];

const UPSELL_TYPE_ICONS = { class: GraduationCap, product: ShoppingBag, experience: Sparkles };

export default function ServicesCRUD({ API, getAuthHeaders, eventId, category, hideSyncConfigCard = false }) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [upsellTemplates, setUpsellTemplates] = useState([]);
  const [hints, setHints] = useState({});
  const [syncConfig, setSyncConfig] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showUpsellDialog, setShowUpsellDialog] = useState(false);
  const [upsellTarget, setUpsellTarget] = useState(null);
  const [upsellForm, setUpsellForm] = useState({ name: "", type: "product", description: "", price: "", placement: "confirmation" });
  // Iter 9.48 - host attaches owned products to a service as add-ons
  const [addonsService, setAddonsService] = useState(null);

  const [form, setForm] = useState({
    name: "", price: "", duration: 60, description: "",
    deposit_required: false, deposit_amount: "",
    variations: [], add_ons: [], tags: []
  });

  const [newVariation, setNewVariation] = useState("");
  const [newVariationPrice, setNewVariationPrice] = useState("");
  const [newAddOn, setNewAddOn] = useState("");
  const [newAddOnPrice, setNewAddOnPrice] = useState("");

  const colors = CATEGORY_COLORS[category] || DEFAULT_COLORS;
  const CategoryIcon = CATEGORY_ICONS[category] || Sparkles;

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (eventId) params.set("event_id", eventId);
      if (category) params.set("category", category);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await fetch(`${API}/provider/services${qs}`, { headers: getAuthHeaders() });
      if (res.ok) setServices(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [API, getAuthHeaders, eventId, category]);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  useEffect(() => {
    if (!category) return;
    fetch(`${API}/services/templates/${category}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setTemplates(data.templates || []);
          setUpsellTemplates(data.upsell_templates || []);
          setHints(data.hints || {});
          setSyncConfig(data.sync_config || null);
        }
      })
      .catch(() => {});
  }, [API, category]);

  const resetForm = () => {
    setForm({ name: "", price: "", duration: 60, description: "", deposit_required: false, deposit_amount: "", variations: [], add_ons: [], tags: [] });
    setEditingService(null);
    setNewVariation(""); setNewVariationPrice("");
    setNewAddOn(""); setNewAddOnPrice("");
  };

  const openAdd = () => { resetForm(); setShowDialog(true); };

  const openEdit = (svc) => {
    setEditingService(svc);
    setForm({
      name: svc.name, price: String(svc.price), duration: svc.duration,
      description: svc.description || "", deposit_required: svc.deposit_required || false,
      deposit_amount: svc.deposit_amount ? String(svc.deposit_amount) : "",
      variations: svc.variations || [], add_ons: svc.add_ons || [], tags: svc.tags || []
    });
    setShowDialog(true);
  };

  const applyTemplate = (tmpl) => {
    setForm({
      name: tmpl.name, price: String(tmpl.price), duration: tmpl.duration,
      description: tmpl.description || "",
      deposit_required: false, deposit_amount: "",
      variations: tmpl.variations || [], add_ons: tmpl.add_ons || []
    });
    setEditingService(null);
    setShowTemplates(false);
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.price) { toast.error("Name and price required"); return; }
    setSaving(true);
    const payload = {
      name: form.name, price: parseFloat(form.price), duration: form.duration,
      description: form.description, deposit_required: form.deposit_required,
      deposit_amount: form.deposit_amount ? parseFloat(form.deposit_amount) : null,
      variations: form.variations, add_ons: form.add_ons,
      tags: form.tags || [],
      event_id: eventId || null,
      category: category || null
    };
    try {
      const url = editingService ? `${API}/provider/services/${editingService.service_id}` : `${API}/provider/services`;
      const method = editingService ? "PUT" : "POST";
      const res = await fetch(url, {
        method, headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        toast.success(editingService ? "Service updated" : "Service added");
        setShowDialog(false); resetForm(); fetchServices();
      } else { const err = await res.json(); toast.error(err.detail || "Failed"); }
    } catch { toast.error("Error saving"); }
    setSaving(false);
  };

  const handleDelete = async (svcId) => {
    if (!window.confirm("Delete this service?")) return;
    try {
      const res = await fetch(`${API}/provider/services/${svcId}`, {
        method: "DELETE", headers: getAuthHeaders()
      });
      if (res.ok) { toast.success("Deleted"); fetchServices(); }
    } catch { toast.error("Error"); }
  };

  const addVariation = () => {
    if (!newVariation) return;
    setForm(f => ({
      ...f, variations: [...f.variations, { name: newVariation, price_modifier: parseFloat(newVariationPrice) || 0 }]
    }));
    setNewVariation(""); setNewVariationPrice("");
  };

  const removeVariation = (idx) => setForm(f => ({ ...f, variations: f.variations.filter((_, i) => i !== idx) }));

  const addAddOn = () => {
    if (!newAddOn) return;
    setForm(f => ({
      ...f, add_ons: [...f.add_ons, { name: newAddOn, price: parseFloat(newAddOnPrice) || 0 }]
    }));
    setNewAddOn(""); setNewAddOnPrice("");
  };

  const removeAddOn = (idx) => setForm(f => ({ ...f, add_ons: f.add_ons.filter((_, i) => i !== idx) }));

  // --- Upsell management ---
  const openUpsellDialog = (svc) => {
    setUpsellTarget(svc);
    setUpsellForm({ name: "", type: "product", description: "", price: "", placement: "confirmation" });
    setShowUpsellDialog(true);
  };

  const applyUpsellTemplate = (tmpl) => {
    setUpsellForm({
      name: tmpl.name, type: tmpl.type, description: tmpl.description || "",
      price: String(tmpl.price || 0), placement: "confirmation"
    });
  };

  const handleSaveUpsell = async () => {
    if (!upsellForm.name || !upsellTarget) return;
    try {
      const res = await fetch(`${API}/provider/services/${upsellTarget.service_id}/upsells`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          name: upsellForm.name, type: upsellForm.type,
          description: upsellForm.description,
          price: parseFloat(upsellForm.price) || 0,
          placement: upsellForm.placement
        })
      });
      if (res.ok) {
        toast.success("Upsell added");
        setShowUpsellDialog(false);
        fetchServices();
      } else { toast.error("Failed to add upsell"); }
    } catch { toast.error("Error"); }
  };

  const handleDeleteUpsell = async (svcId, upsellId) => {
    try {
      const res = await fetch(`${API}/provider/services/${svcId}/upsells/${upsellId}`, {
        method: "DELETE", headers: getAuthHeaders()
      });
      if (res.ok) { toast.success("Upsell removed"); fetchServices(); }
    } catch { toast.error("Error"); }
  };

  const categoryLabel = (category || "").charAt(0).toUpperCase() + (category || "").slice(1);

  return (
    <div className="space-y-5" data-testid="services-crud">
      {/* Category Sync Config Card */}
      {syncConfig && !hideSyncConfigCard && (
        <Card className={`${colors.bg} ${colors.border}`} data-testid="sync-config-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Layers className={`w-4 h-4 ${colors.text}`} />
              <h4 className="text-sm font-semibold">How preferences flow into your bookings</h4>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              When a client books with you, Phygital auto-collects the right preferences and inspo so you walk into every appointment fully prepped. You can edit what's collected anytime.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-white/60 rounded-lg p-3">
                <p className="text-[11px] font-semibold text-gray-700 mb-1">What you ask each client</p>
                <p className="text-[10px] text-gray-500 mb-2">Pre-filled questions about face shape, skin type, lash and lip preferences, and any allergies or sensitivities.</p>
                <div className="flex flex-wrap gap-1">
                  {(syncConfig.requested_defaults || []).map(d => (
                    <Badge key={d} variant="outline" className={`text-[10px] capitalize ${colors.border} ${colors.text}`}>
                      {d.replace(/_/g, " ")}
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="bg-white/60 rounded-lg p-3">
                <p className="text-[11px] font-semibold text-gray-700 mb-1">Inspo your clients can share</p>
                <p className="text-[10px] text-gray-500 mb-2">Pinterest-style mood boards your clients can upload before the appointment, so you arrive aligned on the look.</p>
                <div className="flex flex-wrap gap-1">
                  {(syncConfig.inspo_folders || []).map(f => (
                    <Badge key={f} variant="outline" className={`text-[10px] capitalize ${colors.border} ${colors.text}`}>{f.replace(/_/g, " ")}</Badge>
                  ))}
                </div>
              </div>
              <div className="bg-white/60 rounded-lg p-3">
                <p className="text-[11px] font-semibold text-gray-700 mb-1">Client card style</p>
                <p className="text-[10px] text-gray-500 mb-2">The branded preference card your clients see and share. You'll get one per client per appointment.</p>
                <Badge variant="secondary" className="text-[10px] capitalize">{(syncConfig.card_label || "Beauty Client Card").toLowerCase().replace(/_/g, " ")}</Badge>
              </div>
              <div className="bg-white/60 rounded-lg p-3">
                <p className="text-[11px] font-semibold text-gray-700 mb-1">Extras you'll collect</p>
                <p className="text-[10px] text-gray-500 mb-2">Optional details to keep every client safe and comfortable.</p>
                <div className="flex flex-wrap gap-1">
                  {syncConfig.collect_dietary && <Badge variant="outline" className="text-[10px]">Dietary preferences</Badge>}
                  {syncConfig.collect_accessibility && <Badge variant="outline" className="text-[10px]">Accessibility needs</Badge>}
                  {!syncConfig.collect_dietary && !syncConfig.collect_accessibility && <span className="text-[10px] text-gray-400">None</span>}
                </div>
              </div>
            </div>
            <p className="text-[10px] text-gray-400 mt-3">
              Admin-controlled defaults. Edit in <code>/admin/product → /me Template</code> for your category.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <CategoryIcon className={`w-5 h-5 ${colors.text}`} />
            {categoryLabel ? `${categoryLabel} Services` : "Services"}
          </h3>
          <p className="text-sm text-gray-500">Core services: add classes & products as upsells on each service</p>
        </div>
        <div className="flex gap-2">
          {templates.length > 0 && (
            <Button variant="outline" onClick={() => setShowTemplates(true)} className={`gap-2 ${colors.border} ${colors.text}`} data-testid="use-templates-btn">
              <Copy className="w-4 h-4" /> Templates
            </Button>
          )}
          <Button onClick={openAdd} className="gap-2" data-testid="add-service-btn">
            <Plus className="w-4 h-4" /> Add Service
          </Button>
        </div>
      </div>

      {/* Service List */}
      {loading ? (
        <div className="flex justify-center py-12 text-gray-400">Loading services...</div>
      ) : services.length === 0 ? (
        <Card className={`border-dashed ${colors.border}`}>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className={`w-14 h-14 rounded-2xl ${colors.accent} flex items-center justify-center mb-4`}>
              <CategoryIcon className={`w-7 h-7 ${colors.text}`} />
            </div>
            <p className="font-medium text-gray-700">No {categoryLabel.toLowerCase()} services yet</p>
            <p className="text-sm text-gray-500 mt-1 text-center max-w-sm">
              {category === "makeup" && "Add services like Full Glam, Bridal, or Touch-Up. Classes & products go as upsells."}
              {category === "hair" && "Add services like Braids, Silk Press, or Color. Link classes & products as upsells."}
              {category === "culinary" && "Add services like Private Chef, Meal Prep, or Catering. Cooking classes go as upsells."}
              {!["makeup", "hair", "culinary"].includes(category) && "Add your first service to start accepting bookings"}
            </p>
            <div className="flex gap-2 mt-4">
              {templates.length > 0 && (
                <Button onClick={() => setShowTemplates(true)} variant="outline" className={`gap-2 ${colors.border} ${colors.text}`} data-testid="empty-use-templates-btn">
                  <Copy className="w-4 h-4" /> Start from Templates
                </Button>
              )}
              <Button onClick={openAdd} variant="outline" className="gap-2">
                <Plus className="w-4 h-4" /> Create Custom
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {services.map(svc => (
            <Card key={svc.service_id} className={`hover:shadow-sm transition-shadow ${colors.border}`} data-testid={`service-${svc.service_id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-medium">{svc.name}</h4>
                      {svc.is_active === false && <Badge variant="outline" className="text-xs text-red-500">Inactive</Badge>}
                      {svc.deposit_required && <Badge variant="outline" className="text-xs">Deposit: ${svc.deposit_amount}</Badge>}
                      {category && <Badge className={`text-xs ${colors.accent} ${colors.text} border-0`}>{categoryLabel}</Badge>}
                      {/* Iter 9.48 - service tags (bridal / instructor / travel) */}
                      {(svc.tags || []).map(t => (
                        <Badge
                          key={t}
                          variant="outline"
                          className={`text-[10px] capitalize ${
                            t === 'bridal' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                            t === 'instructor' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' :
                            t === 'travel' ? 'bg-sky-50 text-sky-700 border-sky-200' :
                            'bg-gray-50 text-gray-700 border-gray-200'
                          }`}
                          data-testid={`service-tag-${svc.service_id}-${t}`}
                        >
                          {t}
                        </Badge>
                      ))}
                    </div>
                    {svc.description && <p className="text-sm text-gray-500 mt-1">{svc.description}</p>}
                    {/* Portfolio Images */}
                    {svc.portfolio_images?.length > 0 && (
                      <div className="flex gap-1.5 mt-2">
                        {svc.portfolio_images.slice(0, 4).map((img, idx) => (
                          <div key={idx} className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 border">
                            <img src={img} alt={`${svc.name} portfolio ${idx + 1}`} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                          </div>
                        ))}
                        {svc.portfolio_images.length > 4 && (
                          <div className="w-12 h-12 rounded-lg bg-gray-100 border flex items-center justify-center">
                            <span className="text-xs text-gray-500">+{svc.portfolio_images.length - 4}</span>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-4 mt-2">
                      <span className="flex items-center gap-1 text-sm">
                        <DollarSign className="w-3.5 h-3.5 text-green-600" />${svc.price}
                      </span>
                      <span className="flex items-center gap-1 text-sm text-gray-500">
                        <Clock className="w-3.5 h-3.5" />{svc.duration} min
                      </span>
                      {(svc.variations?.length > 0) && (
                        <span className="flex items-center gap-1 text-sm text-gray-500">
                          <Tag className="w-3.5 h-3.5" />{svc.variations.length} variations
                        </span>
                      )}
                      {(svc.add_ons?.length > 0) && (
                        <span className={`text-xs ${colors.text}`}>{svc.add_ons.length} add-ons</span>
                      )}
                    </div>
                    {/* Upsells on this service */}
                    {svc.upsells?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {svc.upsells.map(u => {
                          const UIcon = UPSELL_TYPE_ICONS[u.type] || Sparkles;
                          return (
                            <div key={u.upsell_id} className="flex items-center gap-1 px-2 py-1 bg-gray-50 border rounded-lg text-xs group">
                              <UIcon className="w-3 h-3 text-gray-400" />
                              <span>{u.name}</span>
                              {u.price > 0 && <span className="text-green-600">${u.price}</span>}
                              <Badge variant="outline" className="text-[10px] px-1 py-0">{u.placement === "both" ? "card+confirm" : u.placement === "preference_card" ? "card" : "confirm"}</Badge>
                              <button onClick={() => handleDeleteUpsell(svc.service_id, u.upsell_id)} className="opacity-0 group-hover:opacity-100 text-red-400 ml-1"><Trash2 className="w-3 h-3" /></button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-gray-500 hover:text-gray-900"
                      onClick={() => openEdit(svc)}
                      title="Edit service"
                      data-testid={`edit-service-${svc.service_id}`}
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDelete(svc.service_id)}
                      title="Delete service"
                      data-testid={`delete-service-${svc.service_id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`service-actions-${svc.service_id}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(svc)}>
                          <Edit2 className="w-3 h-3 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openUpsellDialog(svc)}>
                          <ArrowUpRight className="w-3 h-3 mr-2" /> Add Upsell
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setAddonsService(svc)} data-testid={`addons-service-${svc.service_id}`}>
                          <ShoppingBag className="w-3 h-3 mr-2" /> Add-Ons
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(svc.service_id)}>
                          <Trash2 className="w-3 h-3 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Template Selection Dialog */}
      <Dialog open={showTemplates} onOpenChange={setShowTemplates}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="templates-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CategoryIcon className={`w-5 h-5 ${colors.text}`} />
              {categoryLabel} Service Templates
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500 -mt-2">Core services only. Add classes & products as upsells after.</p>
          <div className="space-y-2 mt-2">
            {templates.map((tmpl, idx) => (
              <button key={idx} onClick={() => applyTemplate(tmpl)} className={`w-full text-left p-3 border rounded-xl hover:shadow-sm transition-all ${colors.border}`} data-testid={`template-${idx}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{tmpl.name}</span>
                  <span className="text-sm font-semibold text-green-700">${tmpl.price}</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">{tmpl.description}</p>
                <div className="flex items-center gap-3 mt-1.5">
                  <span className="text-xs text-gray-400 flex items-center gap-1"><Clock className="w-3 h-3" />{tmpl.duration}min</span>
                  {tmpl.variations?.length > 0 && <span className="text-xs text-gray-400">{tmpl.variations.length} variations</span>}
                  {tmpl.add_ons?.length > 0 && <span className="text-xs text-gray-400">{tmpl.add_ons.length} add-ons</span>}
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Service Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => { if (!open) { setShowDialog(false); resetForm(); } }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CategoryIcon className={`w-4 h-4 ${colors.text}`} />
              {editingService ? "Edit Service" : `Add ${categoryLabel} Service`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Service Name *</Label>
              <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder={hints.name_placeholder || "e.g., Service Name"} data-testid="service-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Price ($) *</Label>
                <Input type="number" value={form.price} onChange={(e) => setForm(f => ({ ...f, price: e.target.value }))} placeholder="0.00" data-testid="service-price-input" />
              </div>
              <div>
                <Label>Duration (min)</Label>
                <select value={form.duration} onChange={(e) => setForm(f => ({ ...f, duration: parseInt(e.target.value) }))} className="w-full h-10 border rounded-md px-3 text-sm" data-testid="service-duration-select">
                  {DURATION_OPTIONS.map(d => <option key={d} value={d}>{d} min ({d >= 60 ? `${Math.floor(d/60)}h${d%60 ? ` ${d%60}m` : ''}` : `${d}m`})</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} placeholder={hints.desc_placeholder || "Describe what's included..."} data-testid="service-desc-input" />
            </div>
            {/* Iter 9.48 - service tags (bridal / instructor / travel) */}
            <div>
              <Label>Tags</Label>
              <p className="text-xs text-gray-500 mb-2">Mark this service for special workflows (e.g. bridal intake, instructor flows, travel pricing).</p>
              <div className="flex gap-2 flex-wrap" data-testid="service-tags-row">
                {["bridal","instructor","travel"].map(tag => {
                  const active = (form.tags || []).includes(tag);
                  return (
                    <button
                      key={tag} type="button"
                      onClick={() => setForm(f => ({ ...f, tags: active ? f.tags.filter(t => t !== tag) : [...(f.tags||[]), tag] }))}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all capitalize ${
                        active && tag === 'bridal' ? 'bg-rose-100 text-rose-700 border-rose-300' :
                        active && tag === 'instructor' ? 'bg-indigo-100 text-indigo-700 border-indigo-300' :
                        active && tag === 'travel' ? 'bg-sky-100 text-sky-700 border-sky-300' :
                        'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`service-tag-toggle-${tag}`}
                    >
                      {tag}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div><p className="text-sm font-medium">Require Deposit</p><p className="text-xs text-gray-500">Collect a deposit when booking</p></div>
              <Switch checked={form.deposit_required} onCheckedChange={(v) => setForm(f => ({ ...f, deposit_required: v }))} data-testid="service-deposit-toggle" />
            </div>
            {form.deposit_required && (
              <div><Label>Deposit Amount ($)</Label><Input type="number" value={form.deposit_amount} onChange={(e) => setForm(f => ({ ...f, deposit_amount: e.target.value }))} placeholder="50.00" data-testid="service-deposit-amount" /></div>
            )}
            <div>
              <Label>Variations (price modifiers)</Label>
              <p className="text-xs text-gray-500 mb-2">{hints.variation_hint || 'e.g., "Option A" +$0, "Option B" +$30'}</p>
              {form.variations.map((v, i) => (
                <div key={i} className="flex items-center gap-2 mb-1">
                  <span className="text-sm flex-1">{v.name}</span>
                  <span className="text-xs text-gray-500">{v.price_modifier >= 0 ? '+' : ''}${v.price_modifier}</span>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => removeVariation(i)}><Trash2 className="w-3 h-3" /></Button>
                </div>
              ))}
              <div className="flex gap-2 mt-1">
                <Input value={newVariation} onChange={(e) => setNewVariation(e.target.value)} placeholder="Variation name" className="flex-1" />
                <Input type="number" value={newVariationPrice} onChange={(e) => setNewVariationPrice(e.target.value)} placeholder="+$" className="w-20" />
                <Button variant="outline" size="sm" onClick={addVariation}>Add</Button>
              </div>
            </div>
            <div>
              <Label>Add-ons</Label>
              <p className="text-xs text-gray-500 mb-2">{hints.addon_hint || "Extra services clients can select"}</p>
              {form.add_ons.map((a, i) => (
                <div key={i} className="flex items-center gap-2 mb-1">
                  <span className="text-sm flex-1">{a.name}</span>
                  <span className="text-xs text-gray-500">${a.price}</span>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => removeAddOn(i)}><Trash2 className="w-3 h-3" /></Button>
                </div>
              ))}
              <div className="flex gap-2 mt-1">
                <Input value={newAddOn} onChange={(e) => setNewAddOn(e.target.value)} placeholder="Add-on name" className="flex-1" />
                <Input type="number" value={newAddOnPrice} onChange={(e) => setNewAddOnPrice(e.target.value)} placeholder="$" className="w-20" />
                <Button variant="outline" size="sm" onClick={addAddOn}>Add</Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDialog(false); resetForm(); }}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-service-btn">
              {saving ? "Saving..." : editingService ? "Save Changes" : "Add Service"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Upsell Dialog */}
      <Dialog open={showUpsellDialog} onOpenChange={setShowUpsellDialog}>
        <DialogContent className="max-w-md" data-testid="upsell-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowUpRight className="w-4 h-4" />
              Add Upsell to "{upsellTarget?.name}"
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500 -mt-2">Classes link to instructor dashboards. Products are standalone. Host chooses where to display.</p>

          {/* Quick-fill from upsell templates */}
          {upsellTemplates.length > 0 && (
            <div className="mb-2">
              <p className="text-xs font-medium text-gray-600 mb-1">Quick-fill:</p>
              <div className="flex flex-wrap gap-1">
                {upsellTemplates.map((ut, idx) => {
                  const UIcon = UPSELL_TYPE_ICONS[ut.type] || Sparkles;
                  return (
                    <button key={idx} onClick={() => applyUpsellTemplate(ut)} className={`flex items-center gap-1 px-2 py-1 border rounded-lg text-xs hover:bg-gray-50 ${colors.border}`} data-testid={`upsell-template-${idx}`}>
                      <UIcon className="w-3 h-3" /> {ut.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="space-y-3">
            <div>
              <Label>Name *</Label>
              <Input value={upsellForm.name} onChange={e => setUpsellForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g., Makeup Class, Product Kit" data-testid="upsell-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <select value={upsellForm.type} onChange={e => setUpsellForm(f => ({ ...f, type: e.target.value }))} className="w-full h-10 border rounded-md px-3 text-sm" data-testid="upsell-type">
                  <option value="class">Class (Instructor)</option>
                  <option value="product">Product</option>
                  <option value="experience">Experience</option>
                </select>
              </div>
              <div>
                <Label>Price ($)</Label>
                <Input type="number" value={upsellForm.price} onChange={e => setUpsellForm(f => ({ ...f, price: e.target.value }))} placeholder="0" data-testid="upsell-price" />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={upsellForm.description} onChange={e => setUpsellForm(f => ({ ...f, description: e.target.value }))} placeholder="Short description" data-testid="upsell-desc" />
            </div>
            <div>
              <Label className="mb-2 block">Where to show</Label>
              <div className="grid grid-cols-3 gap-2">
                {PLACEMENT_OPTIONS.map(p => (
                  <button key={p.id} onClick={() => setUpsellForm(f => ({ ...f, placement: p.id }))} className={`p-2 border rounded-lg text-center transition-all ${upsellForm.placement === p.id ? "border-black bg-gray-50" : "border-gray-200"}`} data-testid={`placement-${p.id}`}>
                    <p className="text-xs font-medium">{p.label}</p>
                    <p className="text-[10px] text-gray-500">{p.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpsellDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveUpsell} disabled={!upsellForm.name} data-testid="save-upsell-btn">Add Upsell</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 9.48 - Add-ons modal */}
      <ServiceAddonsModal
        open={!!addonsService}
        onOpenChange={(o) => { if (!o) setAddonsService(null); }}
        service={addonsService}
        eventId={eventId}
      />
    </div>
  );
}
